/**
 * Reports.js
 *
 * Staff performance figures for management, behind a two-person code gate.
 *
 * WHERE THE REAL GATE IS
 *   Not here. This page draws the lock and collects the code, but every
 *   decision is the server's: task_reports.php works out who is asking from the
 *   session, refuses anyone outside management, and returns NO figures at all
 *   until a code has been verified. That matters because the generic
 *   getData/getDatas API is open to any logged-in client - if this page
 *   computed the numbers itself, the lock would be decoration. So the page
 *   holds no data of its own and asks the endpoint for everything.
 *
 * HOW THE CODE WORKS
 *   Opening the page asks the server for a code. The server emails it to the
 *   OTHER members of management, never to the person asking, so the figures
 *   cannot be pulled by one person alone. The code lasts 30 minutes, and
 *   re-opening the page inside that window reuses the one already out rather
 *   than emailing everybody again.
 */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  Avatar,
  Box,
  Grid,
  Stack,
  Chip,
  Button,
  IconButton,
  Divider,
  Typography,
  Tooltip,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  LinearProgress,
  CircularProgress,
  Alert,
  AlertTitle,
  Container,
  Stepper,
  Step,
  StepLabel,
  useTheme,
  alpha,
} from "@mui/material";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { tokens } from "../../theme";
import { buildUi, SectionCard, StatCard, initials, STAFF_IMAGE_BASE } from "./taskShared";
import StaffReport from "./StaffReport";
import { RatingBadge } from "./TaskRatings";

import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import LockOpenOutlinedIcon from "@mui/icons-material/LockOpenOutlined";
import MarkEmailReadOutlinedIcon from "@mui/icons-material/MarkEmailReadOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import KeyOutlinedIcon from "@mui/icons-material/KeyOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";

const API_BASE = "https://mindsinaction.com.na/api/mindsbackend";

/** Call one action on the reports endpoint. */
const callReports = async (action, extra = {}) => {
  const form = new FormData();
  form.append("action", action);
  form.append("token", process.env.REACT_APP_API_TOKEN);
  Object.entries(extra).forEach(([k, v]) => form.append(k, v));

  try {
    const res = await axios.post(`${API_BASE}/task_reports.php`, form, {
      withCredentials: true,
    });
    return res?.data ?? { success: false, message: "No response" };
  } catch (err) {
    // The endpoint answers 401/403/410 with a JSON body that explains why, and
    // that explanation is more use than "Request failed".
    const body = err?.response?.data;
    if (body && typeof body === "object") return body;
    return { success: false, message: err?.message || "Request failed" };
  }
};

const mmss = (seconds) => {
  const s = Math.max(0, Number(seconds) || 0);
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, "0")}`;
};

/** Status colours: fixed, and never the only signal - see Rate below. */
const TONE = { good: "#0ca30c", warning: "#fab219", critical: "#d03b3b" };

/**
 * A percentage that knows the difference between zero and "no basis".
 *
 * The number stays in ink and a thin meter carries the judgement. It used to
 * colour the number itself, which made colour the only signal and put light
 * status hues on the surface as text, where they are hard to read.
 */
const Rate = ({ ui, value }) => {
  if (value === null || value === undefined) {
    return (
      <Tooltip title="Nothing in this period to measure">
        <Typography variant="body2" sx={{ color: ui.textSecondary }}>
          —
        </Typography>
      </Tooltip>
    );
  }
  const v = Number(value);
  const tone = v >= 75 ? TONE.good : v <= 40 ? TONE.critical : TONE.warning;
  return (
    <Stack alignItems="flex-end" spacing={0.6}>
      <Typography variant="body2" fontWeight={700} sx={{ color: ui.textPrimary, lineHeight: 1 }}>
        {v}%
      </Typography>
      <Box sx={{ width: 56, height: 4, borderRadius: 2, bgcolor: alpha(ui.textSecondary, 0.16), overflow: "hidden" }}>
        <Box sx={{ width: `${Math.max(0, Math.min(100, v))}%`, height: "100%", bgcolor: tone, borderRadius: 2 }} />
      </Box>
    </Stack>
  );
};

/** The index's band, in words as well as colour. */
const indexTier = (v) =>
  v >= 75
    ? { label: "Strong", color: TONE.good }
    : v >= 45
    ? { label: "Fair", color: TONE.warning }
    : { label: "Needs attention", color: TONE.critical };

const COLUMNS = [
  { key: "name", label: "Staff", numeric: false },
  { key: "department", label: "Dept", numeric: false },
  { key: "tasks_assigned", label: "Assigned", numeric: true },
  { key: "tasks_completed", label: "Completed", numeric: true },
  { key: "on_time_rate", label: "On time", numeric: true },
  { key: "avg_days_vs_deadline", label: "Avg days vs due", numeric: true },
  { key: "logging_rate", label: "Progress logged", numeric: true },
  { key: "subtask_rate", label: "Subtasks done", numeric: true },
  { key: "open_now", label: "Open", numeric: true },
  { key: "overdue_now", label: "Overdue", numeric: true },
  { key: "hours_logged", label: "Hours", numeric: true },
  { key: "rating_avg", label: "Rating", numeric: true },
  { key: "index", label: "Index", numeric: true },
];

/** "Tasks 4.0 · Subtasks 6.0 · Daily reports 3.3", leaving out what was not rated. */
const ratingParts = (r) =>
  [
    ["Tasks", r.rating_task_avg],
    ["Subtasks", r.rating_subtask_avg],
    ["Daily reports", r.rating_progress_avg],
  ]
    .filter(([, v]) => v !== null && v !== undefined)
    .map(([label, v]) => `${label} ${Number(v).toFixed(1)}`)
    .join(" · ");

/** The rating cell: stars to read, the detail on hover, low ratings in words. */
const RatingCell = ({ ui, r }) => {
  if (!r.ratings_count) {
    return (
      <Tooltip title="None of their work in this period has been rated - not the same as a low rating">
        <Typography variant="body2" sx={{ color: ui.textSecondary }}>
          —
        </Typography>
      </Tooltip>
    );
  }
  const pieces = `${r.rated_items} piece${r.rated_items === 1 ? "" : "s"} of work`;
  return (
    <Tooltip
      title={
        <>
          {`${r.ratings_count} rating${r.ratings_count === 1 ? "" : "s"} on ${pieces}, out of 6 stars`}
          <br />
          {ratingParts(r)}
        </>
      }
    >
      <Stack alignItems="flex-end" spacing={0.3}>
        <RatingBadge ui={ui} average={r.rating_avg} count={r.ratings_count} />
        {r.rated_low > 0 && (
          <Stack direction="row" spacing={0.4} alignItems="center">
            <ReportProblemOutlinedIcon aria-hidden="true" sx={{ fontSize: 13, color: TONE.critical }} />
            <Typography variant="caption" color={ui.textSecondary} sx={{ whiteSpace: "nowrap" }}>
              {r.rated_low} rated 2 or less
            </Typography>
          </Stack>
        )}
      </Stack>
    </Tooltip>
  );
};

/* ------------------------------------------------------------------ */
/*  Code entry                                                         */
/* ------------------------------------------------------------------ */

const CODE_LENGTH = 8;

/**
 * While somebody is using the page, check in this often. Each check extends the
 * unlock on the server to 30 minutes from now; with no input for this long,
 * the page stops checking in and the unlock runs down.
 */
const REPORT_HEARTBEAT_MS = 2 * 60 * 1000;

/**
 * The alphabet task_reports.php generates codes from. 0, O, 1, I and L are
 * never used - they are too easy to confuse when a code is read out over the
 * phone - so they are refused as you type rather than accepted and rejected
 * later as a wrong attempt.
 */
const CODE_CHAR = /[ABCDEFGHJKMNPQRSTUVWXYZ23456789]/;

const cleanCode = (raw) =>
  String(raw || "")
    .toUpperCase()
    .split("")
    .filter((c) => CODE_CHAR.test(c))
    .join("");

/**
 * Eight single-character boxes.
 *
 * The value is kept as one contiguous string, so there are never holes: a
 * click on an empty box further along moves focus back to the first empty one.
 * Typing advances, Backspace steps back, the arrow keys move, and pasting the
 * whole code - with or without a dash or spaces - fills every box at once.
 * onComplete fires on the eighth character.
 *
 * Module scope, not inside Reports: declared inside, it would be a new
 * component on every render and each box would lose focus after a keystroke.
 */
export const OtpInput = ({ ui, value, onChange, onComplete, disabled, error }) => {
  const refs = useRef([]);

  /**
   * The latest value, readable from event handlers that fire BEFORE React has
   * re-rendered. Moving focus is synchronous and a render is not: reading the
   * `value` prop there sees the previous keystroke's value, which is exactly
   * what made focus bounce back a box and drop every other character.
   */
  const latest = useRef(value);
  latest.current = value;

  // Where focus should land once the value it depends on has rendered.
  const pendingFocus = useRef(null);

  const chars = Array.from({ length: CODE_LENGTH }, (_, i) => value[i] || "");

  const focusAt = (i) => {
    const el = refs.current[Math.max(0, Math.min(CODE_LENGTH - 1, i))];
    if (el) el.focus();
  };

  // Land in the first empty box on mount, and again after a wrong code clears
  // the boxes (the parent remounts this to replay the shake).
  useEffect(() => {
    if (!disabled) focusAt(latest.current.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Apply a focus move only after the new value is on screen.
  useLayoutEffect(() => {
    if (pendingFocus.current !== null) {
      const i = pendingFocus.current;
      pendingFocus.current = null;
      focusAt(i);
    }
  }, [value]);

  const update = (next, focusIndex) => {
    const unchanged = next === latest.current;
    latest.current = next;
    onChange(next);
    // No re-render is coming if nothing changed, so move now; otherwise wait.
    if (unchanged) focusAt(focusIndex);
    else pendingFocus.current = focusIndex;
  };

  const commit = (next) => {
    const v = next.slice(0, CODE_LENGTH);
    update(v, Math.min(v.length, CODE_LENGTH - 1));
    if (v.length === CODE_LENGTH) onComplete(v);
  };

  const handleChange = (i, raw) => {
    const typed = cleanCode(raw);
    if (!typed) return; // a refused character: leave the box as it was
    const cur = latest.current;
    // Several characters at once means autofill or a mobile paste.
    commit(cur.slice(0, i) + typed + (typed.length === 1 ? cur.slice(i + 1) : ""));
  };

  const handleKeyDown = (i, e) => {
    const cur = latest.current;
    if (e.key === "Backspace") {
      e.preventDefault();
      if (cur[i]) {
        update(cur.slice(0, i) + cur.slice(i + 1), i);
      } else if (i > 0) {
        update(cur.slice(0, i - 1) + cur.slice(i), i - 1);
      }
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      focusAt(i - 1);
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      focusAt(Math.min(i + 1, cur.length));
    } else if (e.key === "Enter" && cur.length === CODE_LENGTH) {
      onComplete(cur);
    }
  };

  const handlePaste = (e) => {
    // Some browsers and mobile keyboards give a paste event no clipboardData.
    // Then let the browser paste normally - onChange takes multi-character
    // input - rather than cancelling it and losing the paste altogether.
    const text = e.clipboardData?.getData?.("text");
    if (typeof text !== "string") return;
    e.preventDefault();
    commit(cleanCode(text));
  };

  return (
    <Stack
      direction="row"
      spacing={{ xs: 0.6, sm: 1 }}
      justifyContent="center"
      alignItems="center"
      role="group"
      aria-label="Access code"
    >
      {chars.map((c, i) => (
        <Box key={i} sx={{ display: "flex", alignItems: "center" }}>
          {/* A gap between the halves makes eight characters easy to check. */}
          {i === CODE_LENGTH / 2 && (
            <Box
              aria-hidden
              sx={{ width: { xs: 6, sm: 10 }, height: 2, bgcolor: alpha(ui.textSecondary, 0.45), mr: { xs: 0.6, sm: 1 }, borderRadius: 1 }}
            />
          )}
          <Box
            component="input"
            ref={(el) => {
              refs.current[i] = el;
            }}
            value={c}
            disabled={disabled}
            inputMode="text"
            autoComplete={i === 0 ? "one-time-code" : "off"}
            autoCapitalize="characters"
            spellCheck={false}
            aria-label={`Character ${i + 1} of ${CODE_LENGTH}`}
            onFocus={(e) => {
              // latest, not value: see the note at the top.
              if (i > latest.current.length) focusAt(latest.current.length);
              else e.target.select();
            }}
            onChange={(e) => handleChange(i, e.target.value)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            onPaste={handlePaste}
            sx={{
              width: { xs: 34, sm: 48 },
              height: { xs: 46, sm: 58 },
              p: 0,
              textAlign: "center",
              fontFamily: "'JetBrains Mono', 'SFMono-Regular', Consolas, monospace",
              fontSize: { xs: 19, sm: 24 },
              fontWeight: 700,
              color: ui.textPrimary,
              caretColor: ui.accent,
              bgcolor: c ? alpha(ui.accent, 0.06) : ui.surfaceAlt,
              border: `1.5px solid ${error ? "#d32f2f" : c ? alpha(ui.accent, 0.55) : ui.border}`,
              borderRadius: "12px",
              outline: "none",
              transition: "border-color .15s ease, box-shadow .15s ease, background-color .15s ease",
              "&:focus": {
                borderColor: error ? "#d32f2f" : ui.accent,
                boxShadow: `0 0 0 4px ${alpha(error ? "#d32f2f" : ui.accent, 0.18)}`,
                bgcolor: ui.surface,
              },
              "&:disabled": { opacity: 0.6 },
            }}
          />
        </Box>
      ))}
    </Stack>
  );
};

/** Attempts as dots: used ones red, remaining ones green. */
const AttemptDots = ({ ui, used, max }) => {
  const left = Math.max(0, max - used);
  return (
    <Stack direction="row" spacing={0.6} alignItems="center">
      {Array.from({ length: max }).map((_, i) => (
        <Box
          key={i}
          sx={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            bgcolor: i < used ? "#d32f2f" : alpha("#2e7d32", 0.6),
          }}
        />
      ))}
      <Typography variant="caption" sx={{ ml: 0.8, fontWeight: 600 }} color={left <= 2 ? "#d32f2f" : ui.textSecondary}>
        {left} of {max} attempts left
      </Typography>
    </Stack>
  );
};

const Reports = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";
  const ui = useMemo(() => buildUi(colors, isDark), [colors, isDark]);

  const [checking, setChecking] = useState(true);
  const [granted, setGranted] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);

  const [codePending, setCodePending] = useState(false);
  const [codeExpiresAt, setCodeExpiresAt] = useState(null);
  const [releasedTo, setReleasedTo] = useState("");
  const [attempts, setAttempts] = useState({ used: 0, max: 5 });

  const [codeInput, setCodeInput] = useState("");
  const [requesting, setRequesting] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [gateError, setGateError] = useState("");
  // A wrong code turns the boxes red and replays the shake (via the key).
  const [codeError, setCodeError] = useState(false);
  const [shakeKey, setShakeKey] = useState(0);

  const [report, setReport] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);

  const todayStr = new Date().toISOString().slice(0, 10);
  const ninetyAgo = new Date(Date.now() - 90 * 86400000)
    .toISOString()
    .slice(0, 10);
  const [from, setFrom] = useState(ninetyAgo);
  const [to, setTo] = useState(todayStr);

  // The person whose full report is open, or null.
  const [viewStaff, setViewStaff] = useState(null);

  const [sortKey, setSortKey] = useState("index");
  const [sortDir, setSortDir] = useState("desc");

  /* ------------------------------------------------ the gate ------------ */

  const refreshStatus = useCallback(async () => {
    const res = await callReports("status");
    setChecking(false);

    if (!res?.success) {
      setGateError(res?.message || "Could not check your access");
      return;
    }
    const d = res.data || {};
    setGranted(!!d.granted);
    setSecondsLeft(Number(d.seconds_left) || 0);
    setCodePending(!!d.code_pending);
    setCodeExpiresAt(d.code_expires_at || null);
    setAttempts({
      used: Number(d.attempts_used) || 0,
      max: Number(d.attempts_max) || 5,
    });
  }, []);

  useEffect(() => {
    refreshStatus();
  }, [refreshStatus]);

  // Count down once a second while unlocked. One timer for the whole unlock -
  // this used to tear down and rebuild its timer every second.
  useEffect(() => {
    if (!granted) return undefined;
    const t = setInterval(() => setSecondsLeft((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [granted]);

  // Reaching zero locks the page rather than leaving figures on screen that the
  // server would no longer serve. Kept out of the state updater above: React
  // may run an updater twice, and a toast in there could fire twice.
  useEffect(() => {
    if (granted && secondsLeft <= 0) {
      setGranted(false);
      setReport(null);
      setViewStaff(null);
      toast.info("Report locked - it was not used for 30 minutes");
    }
  }, [granted, secondsLeft]);

  /**
   * Keep the unlock alive while the page is being USED.
   *
   * Input on this page (mouse, keys, scroll, touch) marks it in use. Every two
   * minutes, if there was input since the last check and the tab is in front,
   * a status request extends the unlock on the server and resets the countdown.
   * Leave it alone - or work in another tab - and the checks stop, the
   * countdown runs out, and it locks. That is the point: it is "30 minutes
   * without using the report", not "30 minutes after the code".
   */
  const lastInputRef = useRef(0);
  const lastBeatRef = useRef(0);
  useEffect(() => {
    if (!granted) return undefined;
    // Unlocking is not "use since the last check" - only real input after it is.
    const start = Date.now();
    lastInputRef.current = start;
    lastBeatRef.current = start;
    const mark = () => {
      lastInputRef.current = Date.now();
    };
    const events = ["mousedown", "mousemove", "keydown", "scroll", "wheel", "touchstart"];
    events.forEach((ev) => window.addEventListener(ev, mark, { passive: true }));

    const beat = setInterval(async () => {
      if (document.visibilityState !== "visible") return;
      // Only if there has been input SINCE the previous check. Testing "input
      // in the last two minutes" counted the unlock itself, so walking away
      // straight after unlocking still bought an extra extension.
      if (lastInputRef.current <= lastBeatRef.current) return;
      lastBeatRef.current = Date.now();
      const res = await callReports("status");
      if (!res?.success) return;
      if (res.data?.granted) {
        setSecondsLeft(Number(res.data.seconds_left) || 0);
      } else {
        // Ended elsewhere - locked in another tab, or it ran out.
        setGranted(false);
        setReport(null);
        setViewStaff(null);
      }
    }, REPORT_HEARTBEAT_MS);

    return () => {
      events.forEach((ev) => window.removeEventListener(ev, mark));
      clearInterval(beat);
    };
  }, [granted]);

  const requestCode = useCallback(async () => {
    setRequesting(true);
    setGateError("");
    const res = await callReports("request_code");
    setRequesting(false);

    if (!res?.success) {
      setGateError(res?.message || "Could not request a code");
      return;
    }
    const d = res.data || {};
    setCodePending(true);
    setCodeExpiresAt(d.expires_at || null);
    setReleasedTo(d.released_to || "");
    setAttempts((a) => ({ ...a, max: Number(d.attempts_max) || a.max }));
    toast.info(res.message || "A code has been sent to management");
  }, []);

  // Takes the code directly when called from the 8th box, because the state
  // update for that last character has not landed yet at that moment.
  const verifyCode = useCallback(async (override) => {
    const code = (typeof override === "string" ? override : codeInput).trim();
    if (!code) {
      setGateError("Enter the code a colleague gave you");
      return;
    }
    setVerifying(true);
    setGateError("");
    const res = await callReports("verify", { code });
    setVerifying(false);

    if (!res?.success) {
      setGateError(res?.message || "That code was not accepted");
      setCodeError(true);
      setShakeKey((k) => k + 1);
      const d = res?.data || {};
      if (d.attempts_used !== undefined) {
        setAttempts({
          used: Number(d.attempts_used) || 0,
          max: Number(d.attempts_max) || attempts.max,
        });
      }
      // The code is spent or dead - make them ask for a new one.
      if (res?.message && /expired|used|Too many/i.test(res.message)) {
        setCodePending(false);
      }
      setCodeInput("");
      return;
    }

    setCodeInput("");
    setCodeError(false);
    setCodePending(false);
    setGranted(true);
    setSecondsLeft(Number(res.data?.seconds_left) || 0);
    toast.success(res.message || "Unlocked");
  }, [codeInput, attempts.max]);

  const lock = useCallback(async () => {
    await callReports("lock");
    setGranted(false);
    setReport(null);
    setSecondsLeft(0);
    toast.info("Report locked");
  }, []);

  /* ---------------------------------------------- the figures ----------- */

  const loadReport = useCallback(async () => {
    setLoadingReport(true);
    const res = await callReports("report", { from, to });
    setLoadingReport(false);

    if (!res?.success) {
      // Most likely the grant expired between loads.
      setGateError(res?.message || "Could not load the report");
      if (/Locked|code is needed/i.test(res?.message || "")) {
        setGranted(false);
      }
      return;
    }
    setReport(res.data);
    setSecondsLeft(Number(res.data?.seconds_left) || 0);
  }, [from, to]);

  useEffect(() => {
    if (granted && !report) loadReport();
  }, [granted, report, loadReport]);

  /* ------------------------------------------------ sorting ------------- */

  const sorted = useMemo(() => {
    const rows = [...(report?.staff || [])];
    const col = COLUMNS.find((c) => c.key === sortKey);
    rows.sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      // Nulls always sink: "no basis to judge" is not a low score.
      if (av === null || av === undefined) return 1;
      if (bv === null || bv === undefined) return -1;
      if (col && !col.numeric) {
        return sortDir === "asc"
          ? String(av).localeCompare(String(bv))
          : String(bv).localeCompare(String(av));
      }
      return sortDir === "asc" ? av - bv : bv - av;
    });
    return rows;
  }, [report, sortKey, sortDir]);

  const totals = useMemo(() => {
    const rows = report?.staff || [];
    const sum = (k) => rows.reduce((n, r) => n + (Number(r[k]) || 0), 0);
    const assigned = sum("tasks_assigned");
    const completed = sum("tasks_completed");
    const onTime = sum("completed_on_time");
    return {
      people: rows.filter((r) => r.tasks_assigned > 0).length,
      assigned,
      completed,
      overdue: sum("overdue_now"),
      onTimeRate: completed > 0 ? Math.round((onTime / completed) * 1000) / 10 : null,
    };
  }, [report]);

  // Where the person is in the three-step flow.
  const gateStep = !codePending ? 0 : codeInput.length > 0 ? 2 : 1;
  const recipients = releasedTo
    .split(",")
    .map((r) => r.trim())
    .filter(Boolean);
  const expiryTime = codeExpiresAt ? String(codeExpiresAt).slice(11, 16) : "";

  const sortBy = (key) => {
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(COLUMNS.find((c) => c.key === key)?.numeric ? "desc" : "asc");
    }
  };

  /* ================================================================== */

  if (checking) {
    return (
      <Box sx={{ p: 6, display: "grid", placeItems: "center", bgcolor: ui.pageBg, minHeight: "100%" }}>
        <CircularProgress sx={{ color: ui.accent }} />
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: ui.pageBg, minHeight: "100%", py: 3 }}>
      <ToastContainer position="top-right" autoClose={4000} theme={isDark ? "dark" : "light"} />
      <Container maxWidth="xl">
        {/* ---------------- Header ---------------- */}
        <Box
          sx={{
            background: ui.gradient,
            borderRadius: "16px",
            p: { xs: 2.5, md: 3.5 },
            mb: 3,
            color: "#fff",
            boxShadow: ui.shadow,
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", md: "center" }}
            gap={2}
          >
            <Box>
              <Typography variant="h4" fontWeight={800}>
                Staff performance
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.92, mt: 0.5 }}>
                Completion, deadline and daily-progress record from the task system
              </Typography>
            </Box>

            {granted && (
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Tooltip title="Stays unlocked while you use the report. Locks after 30 minutes without use, or when you press Lock.">
                  <Chip
                    icon={<LockOpenOutlinedIcon sx={{ color: "#fff !important" }} />}
                    label={`Unlocked · ${mmss(secondsLeft)}`}
                    sx={{
                      bgcolor: alpha("#fff", 0.2),
                      color: "#fff",
                      fontWeight: 700,
                    }}
                  />
                </Tooltip>
                <Button
                  size="small"
                  onClick={lock}
                  startIcon={<LockOutlinedIcon />}
                  sx={{
                    color: "#fff",
                    borderColor: alpha("#fff", 0.5),
                    "&:hover": { borderColor: "#fff", bgcolor: alpha("#fff", 0.12) },
                  }}
                  variant="outlined"
                >
                  Lock
                </Button>
              </Stack>
            )}
          </Stack>
        </Box>

        {/* ================= THE GATE ================= */}
        {!granted && (
          <Box sx={{ display: "flex", justifyContent: "center", py: { xs: 1, md: 3 } }}>
            <Box
              sx={{
                width: "100%",
                maxWidth: 620,
                bgcolor: ui.surface,
                border: `1px solid ${ui.border}`,
                borderRadius: "20px",
                boxShadow: ui.shadow,
                overflow: "hidden",
              }}
            >
              {/* ---------- Header ---------- */}
              <Box
                sx={{
                  px: { xs: 3, md: 4 },
                  pt: { xs: 3.5, md: 4.5 },
                  pb: 3,
                  textAlign: "center",
                  background: `linear-gradient(180deg, ${alpha(ui.accent, 0.09)} 0%, transparent 100%)`,
                }}
              >
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    mx: "auto",
                    mb: 2,
                    borderRadius: "18px",
                    display: "grid",
                    placeItems: "center",
                    color: "#fff",
                    background: ui.gradient,
                    boxShadow: `0 12px 26px -12px ${alpha(ui.accent, 0.8)}`,
                  }}
                >
                  <ShieldOutlinedIcon sx={{ fontSize: 34 }} />
                </Box>
                <Typography variant="h5" fontWeight={800} color={ui.textPrimary}>
                  Two-person access
                </Typography>
                <Typography
                  variant="body2"
                  color={ui.textSecondary}
                  sx={{ mt: 1, maxWidth: 440, mx: "auto", lineHeight: 1.65 }}
                >
                  These figures inform promotion and performance decisions, so nobody can
                  open them alone. A colleague in management has to release the code.
                </Typography>
              </Box>

              {/* ---------- Progress ---------- */}
              <Box
                sx={{
                  px: { xs: 1, md: 4 },
                  pb: 2.5,
                  "& .MuiStepLabel-label": { fontSize: { xs: 11.5, sm: 13 }, mt: "6px !important" },
                  "& .MuiStepIcon-root.Mui-active, & .MuiStepIcon-root.Mui-completed": { color: ui.accent },
                }}
              >
                <Stepper activeStep={gateStep} alternativeLabel>
                  {["Request a code", "Get it from a colleague", "Unlock"].map((label) => (
                    <Step key={label}>
                      <StepLabel>{label}</StepLabel>
                    </Step>
                  ))}
                </Stepper>
              </Box>

              <Divider sx={{ borderColor: ui.border }} />

              {/* ---------- Body ---------- */}
              <Box sx={{ px: { xs: 2.5, md: 4.5 }, py: { xs: 3, md: 3.5 } }}>
                {!codePending ? (
                  /* ===== Step 1: request ===== */
                  <Stack spacing={2.5}>
                    {gateError && (
                      <Alert severity="warning" sx={{ borderRadius: "12px" }} onClose={() => setGateError("")}>
                        {gateError}
                      </Alert>
                    )}

                    <Stack
                      direction="row"
                      spacing={1.5}
                      alignItems="flex-start"
                      sx={{
                        p: 2,
                        borderRadius: "14px",
                        bgcolor: alpha(ui.accent, 0.06),
                        border: `1px solid ${alpha(ui.accent, 0.2)}`,
                      }}
                    >
                      <GroupsOutlinedIcon sx={{ color: ui.accent, mt: 0.2 }} />
                      <Typography variant="body2" color={ui.textPrimary} sx={{ lineHeight: 1.65 }}>
                        A one-time code will be emailed to{" "}
                        <strong>every other active member of management</strong>. Your own
                        address is left off, so you will need to ask one of them for it. The
                        code is valid for <strong>30 minutes</strong> and works once.
                      </Typography>
                    </Stack>

                    <Button
                      fullWidth
                      size="large"
                      variant="contained"
                      onClick={requestCode}
                      disabled={requesting}
                      startIcon={
                        requesting ? (
                          <CircularProgress size={18} sx={{ color: "#fff" }} />
                        ) : (
                          <MarkEmailReadOutlinedIcon />
                        )
                      }
                      sx={{
                        py: 1.5,
                        borderRadius: "12px",
                        fontWeight: 800,
                        fontSize: 15,
                        textTransform: "none",
                        background: ui.gradient,
                        boxShadow: `0 10px 22px -12px ${alpha(ui.accent, 0.9)}`,
                        "&:hover": { background: ui.gradient, filter: "brightness(1.07)" },
                      }}
                    >
                      {requesting ? "Sending the code…" : "Request access code"}
                    </Button>
                  </Stack>
                ) : (
                  /* ===== Steps 2-3: enter ===== */
                  <Stack spacing={2.75} alignItems="center">
                    <Box sx={{ textAlign: "center" }}>
                      <Stack direction="row" spacing={1} justifyContent="center" alignItems="center">
                        <KeyOutlinedIcon sx={{ color: ui.accent }} />
                        <Typography variant="subtitle1" fontWeight={800} color={ui.textPrimary}>
                          Enter the access code
                        </Typography>
                      </Stack>
                      <Typography variant="body2" color={ui.textSecondary} sx={{ mt: 0.75, lineHeight: 1.6 }}>
                        Ask a colleague in management for the 8-character code they were emailed.
                      </Typography>
                    </Box>

                    {recipients.length > 0 && (
                      <Stack direction="row" flexWrap="wrap" justifyContent="center" gap={0.75}>
                        {recipients.map((r) => (
                          <Chip
                            key={r}
                            size="small"
                            label={r}
                            variant="outlined"
                            sx={{ borderColor: ui.border, color: ui.textSecondary }}
                          />
                        ))}
                      </Stack>
                    )}

                    {/* key replays the shake on every wrong code */}
                    <Box
                      key={shakeKey}
                      sx={{
                        "@keyframes miaGateShake": {
                          "0%, 100%": { transform: "translateX(0)" },
                          "20%, 60%": { transform: "translateX(-8px)" },
                          "40%, 80%": { transform: "translateX(8px)" },
                        },
                        animation: shakeKey ? "miaGateShake .45s ease" : "none",
                        "@media (prefers-reduced-motion: reduce)": { animation: "none" },
                      }}
                    >
                      <OtpInput
                        ui={ui}
                        value={codeInput}
                        disabled={verifying}
                        error={codeError}
                        onChange={(v) => {
                          setCodeInput(v);
                          if (codeError) setCodeError(false);
                          if (gateError) setGateError("");
                        }}
                        onComplete={(v) => {
                          if (!verifying) verifyCode(v);
                        }}
                      />
                    </Box>

                    <Typography variant="caption" color={ui.textSecondary} sx={{ mt: "-8px !important" }}>
                      Codes never contain 0, O, 1, I or L.
                    </Typography>

                    {gateError && (
                      <Typography variant="body2" sx={{ color: "#d32f2f", fontWeight: 600, textAlign: "center" }}>
                        {gateError}
                      </Typography>
                    )}

                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={{ xs: 1, sm: 3 }}
                      alignItems="center"
                      justifyContent="center"
                    >
                      <AttemptDots ui={ui} used={attempts.used} max={attempts.max} />
                      {expiryTime && (
                        <Stack direction="row" spacing={0.6} alignItems="center">
                          <ScheduleOutlinedIcon sx={{ fontSize: 16, color: ui.textSecondary }} />
                          <Typography variant="caption" color={ui.textSecondary} sx={{ fontWeight: 600 }}>
                            Valid until {expiryTime}
                          </Typography>
                        </Stack>
                      )}
                    </Stack>

                    <Button
                      fullWidth
                      size="large"
                      variant="contained"
                      onClick={() => verifyCode()}
                      disabled={verifying || codeInput.length < CODE_LENGTH}
                      startIcon={
                        verifying ? (
                          <CircularProgress size={18} sx={{ color: "#fff" }} />
                        ) : (
                          <LockOpenOutlinedIcon />
                        )
                      }
                      sx={{
                        py: 1.5,
                        borderRadius: "12px",
                        fontWeight: 800,
                        fontSize: 15,
                        textTransform: "none",
                        background: ui.gradient,
                        boxShadow: `0 10px 22px -12px ${alpha(ui.accent, 0.9)}`,
                        "&:hover": { background: ui.gradient, filter: "brightness(1.07)" },
                        "&.Mui-disabled": { background: alpha(ui.textSecondary, 0.18), color: alpha(ui.textPrimary, 0.45) },
                      }}
                    >
                      {verifying ? "Checking…" : "Unlock report"}
                    </Button>

                    {/*
                      No "resend" here on purpose: while a code is live the server
                      reuses it and emails nobody, so a resend button would do nothing.
                    */}
                    <Typography variant="caption" color={ui.textSecondary} sx={{ textAlign: "center", lineHeight: 1.6 }}>
                      The code works once, and only for you.
                      {expiryTime ? ` A new one can be requested after ${expiryTime}.` : ""}
                    </Typography>
                  </Stack>
                )}
              </Box>

              {/* ---------- Footer ---------- */}
              <Box
                sx={{
                  px: { xs: 2.5, md: 4 },
                  py: 1.6,
                  bgcolor: ui.surfaceAlt,
                  borderTop: `1px solid ${ui.border}`,
                  display: "flex",
                  gap: 1,
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                }}
              >
                <HistoryOutlinedIcon sx={{ fontSize: 16, color: ui.textSecondary, flexShrink: 0 }} />
                <Typography variant="caption" color={ui.textSecondary}>
                  Every request and every unlock is recorded, with the colleagues the code was sent to.
                </Typography>
              </Box>
            </Box>
          </Box>
        )}

        {/* ================= THE FIGURES ================= */}
        {granted && (
          <>
            {loadingReport && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

            <SectionCard
              ui={ui}
              title="Period"
              hint="Completions and progress logs are counted inside this window. Open and overdue counts are as of today."
              action={
                <Tooltip title="Reload">
                  <IconButton onClick={loadReport} sx={{ color: ui.accent }}>
                    <RefreshOutlinedIcon />
                  </IconButton>
                </Tooltip>
              }
            >
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
                <TextField
                  label="From"
                  type="date"
                  size="small"
                  value={from}
                  onChange={(e) => setFrom(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="To"
                  type="date"
                  size="small"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                <Button variant="outlined" onClick={loadReport} disabled={loadingReport}>
                  Apply
                </Button>
                {report && (
                  <Typography variant="caption" color={ui.textSecondary}>
                    {report.working_days} working days in range
                  </Typography>
                )}
              </Stack>
            </SectionCard>

            {report && (
              <>
                <Grid container spacing={2} sx={{ mb: 1 }}>
                  <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                    <StatCard
                      ui={ui}
                      icon={<FactCheckOutlinedIcon />}
                      label="People with work"
                      value={totals.people}
                      sub={`${totals.assigned} assignments in range`}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                    <StatCard
                      ui={ui}
                      icon={<TaskAltOutlinedIcon />}
                      label="Completed"
                      value={totals.completed}
                      sub="in the selected period"
                      tone="#2e7d32"
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                    <StatCard
                      ui={ui}
                      icon={<ScheduleOutlinedIcon />}
                      label="On time"
                      value={totals.onTimeRate === null ? "—" : `${totals.onTimeRate}%`}
                      sub="of everything completed"
                      tone="#0288d1"
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                    <StatCard
                      ui={ui}
                      icon={<ReportProblemOutlinedIcon />}
                      label="Overdue now"
                      value={totals.overdue}
                      sub="open and past the deadline"
                      tone="#c62828"
                    />
                  </Grid>
                </Grid>

                <Alert severity="info" sx={{ mb: 3, borderRadius: "12px" }}>
                  <AlertTitle sx={{ fontWeight: 700 }}>Read this before using it in a decision</AlertTitle>
                  These figures measure what the task system was told, not everything a
                  colleague did. Work handled outside the system, help given to others, and
                  the difficulty of a task are all invisible here. A dash means there was
                  nothing to measure in this period, which is not the same as a score of
                  zero. Treat this as one input to a conversation, not a ranking.
                </Alert>

                <SectionCard
                  ui={ui}
                  title="By person"
                  hint="Click a column heading to sort, or a person for their full report by day, week, month or year. Index is the average of completion rate, on-time rate and progress-logging rate - deliberately not task volume, which would only reward whoever splits work into the most pieces. Rating is the average stars (out of 6) that top management, leaders and supervisors gave their tasks, subtasks and daily reports in this period, each piece of work counted once. It sits beside the index, not inside it."
                >
                  {/*
                    Sticky header and sticky first and last columns, so a wide
                    table keeps its context when scrolled either way. Every cell
                    has an opaque background - a sticky cell over a transparent
                    one shows the scrolled content through it - and hover is a
                    gradient layered on top, which stays opaque.
                  */}
                  <TableContainer
                    sx={{
                      maxHeight: "70vh",
                      border: `1px solid ${ui.border}`,
                      borderRadius: "12px",
                      "& td, & th": { borderColor: ui.border },
                      "& td": { bgcolor: ui.surface },
                    }}
                  >
                    <Table size="small" stickyHeader sx={{ minWidth: 1290 }}>
                      <TableHead>
                        <TableRow>
                          {COLUMNS.map((c, i) => (
                            <TableCell
                              key={c.key}
                              align={c.numeric ? "right" : "left"}
                              sx={{
                                bgcolor: ui.surfaceAlt,
                                color: ui.textSecondary,
                                fontWeight: 700,
                                fontSize: 11.5,
                                textTransform: "uppercase",
                                letterSpacing: 0.6,
                                whiteSpace: "nowrap",
                                py: 1.4,
                                ...(i === 0 && { position: "sticky", left: 0, zIndex: 3, minWidth: 220 }),
                              }}
                            >
                              <TableSortLabel
                                active={sortKey === c.key}
                                direction={sortKey === c.key ? sortDir : "desc"}
                                onClick={() => sortBy(c.key)}
                              >
                                {c.label}
                              </TableSortLabel>
                            </TableCell>
                          ))}
                          <TableCell
                            align="center"
                            sx={{
                              bgcolor: ui.surfaceAlt,
                              color: ui.textSecondary,
                              fontWeight: 700,
                              fontSize: 11.5,
                              textTransform: "uppercase",
                              letterSpacing: 0.6,
                              position: "sticky",
                              right: 0,
                              zIndex: 3,
                              borderLeft: `1px solid ${ui.border}`,
                            }}
                          >
                            Report
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {sorted.map((r) => {
                          const tier = r.index === null ? null : indexTier(r.index);
                          const late = r.avg_days_vs_deadline;
                          return (
                            <TableRow
                              key={r.staff_id}
                              onClick={() => setViewStaff(r)}
                              sx={{
                                cursor: "pointer",
                                "&:hover td": {
                                  backgroundImage: `linear-gradient(${alpha(ui.accent, 0.07)}, ${alpha(ui.accent, 0.07)})`,
                                },
                                "&:last-of-type td": { borderBottom: 0 },
                              }}
                            >
                              <TableCell sx={{ position: "sticky", left: 0, zIndex: 1, py: 1.3 }}>
                                <Stack direction="row" spacing={1.3} alignItems="center">
                                  {/* Their profile photo; initials if there is none or it fails to load. */}
                                  <Avatar
                                    src={r.image ? `${STAFF_IMAGE_BASE}${r.image}` : undefined}
                                    alt={r.name}
                                    slotProps={{ img: { loading: "lazy" } }}
                                    sx={{
                                      width: 34,
                                      height: 34,
                                      fontSize: 12,
                                      fontWeight: 800,
                                      color: ui.accent,
                                      bgcolor: alpha(ui.accent, 0.14),
                                      flexShrink: 0,
                                    }}
                                  >
                                    {initials(r.name?.split(" ")[0], r.name?.split(" ").slice(-1)[0])}
                                  </Avatar>
                                  <Box sx={{ minWidth: 0 }}>
                                    <Typography variant="body2" fontWeight={700} color={ui.textPrimary} noWrap>
                                      {r.name}
                                    </Typography>
                                    <Typography variant="caption" color={ui.textSecondary} sx={{ textTransform: "capitalize" }}>
                                      {r.role}
                                    </Typography>
                                  </Box>
                                </Stack>
                              </TableCell>
                              <TableCell sx={{ color: ui.textSecondary }}>{r.department || "—"}</TableCell>
                              <TableCell align="right" sx={{ color: ui.textPrimary, fontVariantNumeric: "tabular-nums" }}>
                                {r.tasks_assigned}
                              </TableCell>
                              <TableCell align="right" sx={{ color: ui.textPrimary, fontVariantNumeric: "tabular-nums" }}>
                                {r.tasks_completed}
                              </TableCell>
                              <TableCell align="right">
                                <Rate ui={ui} value={r.on_time_rate} />
                              </TableCell>
                              <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                                {/* Words, not a coloured +/- number. */}
                                <Typography variant="body2" color={late === null ? ui.textSecondary : ui.textPrimary}>
                                  {late === null
                                    ? "—"
                                    : late === 0
                                    ? "On the day"
                                    : late < 0
                                    ? `${Math.abs(late)} d early`
                                    : `${late} d late`}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Tooltip title={`${r.days_logged} of ${r.days_expected} working days with a task running`}>
                                  <span>
                                    <Rate ui={ui} value={r.logging_rate} />
                                  </span>
                                </Tooltip>
                              </TableCell>
                              <TableCell align="right">
                                <Rate ui={ui} value={r.subtask_rate} />
                              </TableCell>
                              <TableCell align="right" sx={{ color: ui.textPrimary, fontVariantNumeric: "tabular-nums" }}>
                                {r.open_now}
                              </TableCell>
                              <TableCell align="right">
                                {r.overdue_now > 0 ? (
                                  <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
                                    <ReportProblemOutlinedIcon aria-label="Overdue" sx={{ fontSize: 16, color: TONE.critical }} />
                                    <Typography variant="body2" fontWeight={700} color={ui.textPrimary}>
                                      {r.overdue_now}
                                    </Typography>
                                  </Stack>
                                ) : (
                                  <Typography variant="body2" color={ui.textSecondary}>0</Typography>
                                )}
                              </TableCell>
                              <TableCell align="right" sx={{ color: ui.textPrimary, fontVariantNumeric: "tabular-nums" }}>
                                {Number(r.hours_logged).toFixed(1)}
                              </TableCell>
                              <TableCell align="right">
                                <RatingCell ui={ui} r={r} />
                              </TableCell>
                              <TableCell align="right">
                                {tier ? (
                                  <Tooltip title={tier.label}>
                                    <Chip
                                      size="small"
                                      label={r.index}
                                      sx={{
                                        fontWeight: 800,
                                        minWidth: 52,
                                        color: ui.textPrimary,
                                        bgcolor: alpha(tier.color, 0.16),
                                        border: `1px solid ${alpha(tier.color, 0.5)}`,
                                      }}
                                    />
                                  </Tooltip>
                                ) : (
                                  <Typography variant="body2" color={ui.textSecondary}>—</Typography>
                                )}
                              </TableCell>
                              <TableCell
                                align="center"
                                sx={{ position: "sticky", right: 0, zIndex: 1, borderLeft: `1px solid ${ui.border}` }}
                              >
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<VisibilityOutlinedIcon sx={{ fontSize: 17 }} />}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setViewStaff(r);
                                  }}
                                  sx={{
                                    textTransform: "none",
                                    fontWeight: 700,
                                    borderRadius: "8px",
                                    borderColor: alpha(ui.accent, 0.5),
                                    color: ui.textPrimary,
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  View
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                        {!sorted.length && (
                          <TableRow>
                            <TableCell
                              colSpan={COLUMNS.length + 1}
                              align="center"
                              sx={{ color: ui.textSecondary, py: 4 }}
                            >
                              Nothing recorded in this period.
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <Divider sx={{ borderColor: ui.border, my: 2 }} />
                  <Typography variant="caption" color={ui.textSecondary}>
                    Opened {report.generated_at}
                    {report.generated_for ? ` by ${report.generated_for}` : ""} · every request
                    for this report is logged with the colleague who released the code.
                  </Typography>
                </SectionCard>
              </>
            )}
          </>
        )}
      </Container>

      {/* Only while unlocked: the grant running out unmounts it with the figures. */}
      {granted && (
        <StaffReport
          open={!!viewStaff}
          staff={viewStaff}
          defaultDate={to}
          ui={ui}
          isDark={isDark}
          fetchReport={async (params) => {
            // Opening a person's report is use of the report: keep the
            // countdown in step with the extension the server just gave.
            const res = await callReports("staff_report", params);
            if (res?.success && res.data?.seconds_left) {
              setSecondsLeft(Number(res.data.seconds_left));
            }
            return res;
          }}
          onLocked={() => {
            setViewStaff(null);
            setGranted(false);
            setReport(null);
            toast.info("Report locked - it was not used for 30 minutes");
          }}
          onClose={() => setViewStaff(null)}
        />
      )}
    </Box>
  );
};

export default Reports;
